import { useState } from 'react';
import { METRIC_META, WEIGHT_GAIN_DAY_LBS, WEIGHT_GAIN_WEEK_LBS } from '@heartcoach/care';
import type { AppSession, PatientEvent, PatientView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, fmtInt } from '../../lib/format';
import { Icon } from '../../components/HeartMark';

export type VitalKind = 'weight' | 'pulse' | 'steps';

const SPEC = {
  weight: { metric: 'weight-lbs', title: 'Log your weight', unit: 'lbs', placeholder: '182.4' },
  pulse: { metric: 'heart-rate', title: 'Take your pulse', unit: 'bpm', placeholder: '68' },
  steps: { metric: null, title: "Log today's steps", unit: 'steps', placeholder: '6,200' },
} as const;

/**
 * The numbers a patient enters themselves: a weight, a resting pulse, a step count.
 *
 * Weight is the earliest heart-failure warning there is and a pulse at the wrist is the AFib signal
 * a watch cannot replace, so both are entered deliberately — which is also what makes them earn,
 * where a device sample does not. Steps were a goal the app tracked and gave no way to fill in:
 * until now the step-goal coins were unearnable unless a device sent them.
 */
export function VitalLogFlow({ session, view, kind, onDone, onClose }: { session: AppSession; view: PatientView; kind: VitalKind; onDone: () => Promise<void>; onClose: () => void }) {
  const spec = SPEC[kind];
  const [value, setValue] = useState('');
  const [irregular, setIrregular] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ coins: number; message: string | null } | null>(null);

  const n = Number(value.replace(/,/g, ''));
  const valid = value.trim() !== '' && Number.isFinite(n) && n >= 0 && (kind !== 'steps' || Number.isInteger(n));
  const last = spec.metric ? view.metrics[spec.metric] : null;
  const stepGoal = view.progress.steps.todayGoal;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const event: PatientEvent =
        kind === 'steps' ? { type: 'steps', count: Math.round(n), source: 'manual' } : { type: 'metric', kind: spec.metric as 'weight-lbs' | 'heart-rate', value: kind === 'weight' ? Math.round(n * 10) / 10 : Math.round(n), source: 'manual' };
      const r = await api.postEvent(event, session.token);
      if (kind === 'pulse' && irregular) {
        await api.postEvent({ type: 'log-symptoms', symptoms: ['irregular-heartbeat'] }, session.token);
      }
      const coins = r.earned.reduce((a, e) => a + e.coins, 0);
      setDone({ coins, message: messageFor(kind, r.view, irregular) });
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={spec.title}>
      <div className="modal">
        <header className="modal-head">
          <h2>{done ? 'Logged' : spec.title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="modal-body">
          {done ? (
            <>
              <div className="result-hero">
                <strong className="huge">
                  {kind === 'steps' ? fmtInt(n) : value} <small>{spec.unit}</small>
                </strong>
              </div>
              {done.coins > 0 ? (
                <p className="earned-strip">
                  <Icon name="coin" size={16} /> +{done.coins} HeartCoins
                </p>
              ) : null}
              {done.message ? <p>{done.message}</p> : null}
            </>
          ) : (
            <>
              {kind === 'weight' ? <p className="hint">Same time each morning, after the bathroom and before breakfast, in similar clothes. That is what makes two days comparable.</p> : null}
              {kind === 'pulse' ? <p className="hint">Sit quietly for five minutes. Find the beat on the thumb side of your wrist, count for thirty seconds and double it.</p> : null}
              {kind === 'steps' ? (
                <p className="hint">Whatever your phone or watch says. We keep the highest number for today, so checking in again later never lowers it.</p>
              ) : null}
              <label className="field">
                <span>
                  {kind === 'steps' ? 'Steps so far today' : METRIC_META[spec.metric as 'weight-lbs' | 'heart-rate'].label} ({spec.unit})
                </span>
                <input inputMode={kind === 'weight' ? 'decimal' : 'numeric'} value={value} placeholder={spec.placeholder} onChange={(e) => setValue(e.target.value.replace(kind === 'weight' ? /[^\d.]/g : /[^\d]/g, ''))} autoFocus />
              </label>
              {kind === 'pulse' ? (
                <label className="check">
                  <input type="checkbox" checked={irregular} onChange={(e) => setIrregular(e.target.checked)} /> It felt irregular or skipped beats
                </label>
              ) : null}
              {last ? (
                <p className="hint small">
                  Last {METRIC_META[spec.metric as 'weight-lbs' | 'heart-rate'].label.toLowerCase()}: {last.value} {spec.unit} · {fmtAgo(last.at)}
                </p>
              ) : null}
              {kind === 'steps' ? (
                <p className="hint small">
                  Logged so far today: {fmtInt(view.stepsToday)}
                  {stepGoal ? ` · goal ${fmtInt(stepGoal)}` : ''}
                </p>
              ) : null}
              {kind === 'weight' ? (
                <p className="hint small">
                  A rise of {WEIGHT_GAIN_DAY_LBS} lb in a day or {WEIGHT_GAIN_WEEK_LBS} lb in a week usually means fluid, and your care team wants to hear about it early.
                </p>
              ) : null}
              {error ? <p className="form-error">{error}</p> : null}
            </>
          )}
        </div>

        <footer className="modal-foot">
          {done ? (
            <button type="button" className="primary" onClick={onClose}>
              Back to home
            </button>
          ) : (
            <button type="button" className="primary big" disabled={busy || !valid} onClick={() => void submit()}>
              {busy ? 'Saving…' : 'Log it'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

/** What the patient is told after the number lands, from their own trend rather than a rule of thumb. */
function messageFor(kind: VitalKind, view: PatientView, irregular: boolean): string | null {
  if (kind === 'steps') {
    const goal = view.progress.steps.todayGoal;
    const today = view.stepsToday;
    if (goal && today >= goal) return `That is your step goal for today, and ${view.progress.steps.daysComplete} of the last 7 days. Walking is the cheapest thing your heart gets.`;
    if (goal) return `${fmtInt(goal - today)} to go to reach ${fmtInt(goal)}. A ten-minute walk after a meal is the easiest way to close it.`;
    return 'Logged. Keep it up: regular walking is one of the best things for your heart.';
  }
  if (kind === 'weight') {
    const w = view.weight;
    if (!w) return null;
    if (w.dayDeltaLbs !== null && w.dayDeltaLbs >= WEIGHT_GAIN_DAY_LBS) {
      return `That is ${w.dayDeltaLbs} lb up on the day before. That usually means fluid: go easy on salt today and call ${view.doctor.name}'s team — this is exactly what they want to catch early.`;
    }
    if (w.weekDeltaLbs !== null && w.weekDeltaLbs >= WEIGHT_GAIN_WEEK_LBS) {
      return `That is ${w.weekDeltaLbs} lb up over the week. Tell ${view.doctor.name}'s team today, and keep weighing each morning.`;
    }
    if (w.dayDeltaLbs !== null) return `Steady: ${w.dayDeltaLbs >= 0 ? 'up' : 'down'} ${Math.abs(w.dayDeltaLbs)} lb on the day before. Same time tomorrow.`;
    return 'Logged. Weigh again tomorrow morning so we have two days to compare.';
  }
  const p = view.pulse;
  if (irregular) return `Logged, and your care team has been told it felt irregular. If it keeps up, or you feel breathless, dizzy or have chest discomfort, call ${view.doctor.name}'s team.`;
  if (p && p.value >= 120) return `${p.value} bpm is fast for a resting pulse. Rest a few minutes and take it again; if it stays up, contact your care team.`;
  if (p && p.value < 50) return `${p.value} bpm is on the slow side. If you feel dizzy, faint or unusually tired, contact your care team — and never change a dose yourself.`;
  return 'Logged. Taking it at the same time each day makes the pattern easy to see.';
}
